import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AppRoutes } from 'routes/app-routes';
import { BaseButton } from 'components/ui/buttons';
import { createNotification } from 'components/ui';
import { LockXLIcon, WarningIcon, WifiOffIcon } from 'assets/icons';
import { LogoDarkIcon, LogoIcon } from 'assets/images';
import { InputField } from 'components/ui/form-fields';
import { useAuth } from 'contexts/AuthContextProvider';
import { useIpAllowList } from 'contexts/IpAllowListContextProvider';
import { useTenancy } from 'contexts/TenantContextProvider';
import { DARK_MODE_CLASS, useTheme } from 'contexts/ThemeContextProvider';
import { useUsersnapApi } from 'contexts/UsersnapContextProvider';
import { minAssessmentsForFirstSurvey } from 'types/assessment';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';
import { getUserTotalCompletedAssessments, loginAuth } from 'services/api';
import { PatientIdFormModal } from 'components/offline/PatientIdFormModal';

type LoginFormValues = {
  id: string;
};

type LoginLocationState = {
  from?: {
    pathname?: string;
    search?: string;
    hash?: string;
  };
};

export const Login: React.FC = () => {
  const [submitting, setSubmitting] = useState(false);
  const [isShowPatientIdFormModal, setShowPatientIdFormModal] = useState(false);
  const [apiError, setApiError] = useState<string | undefined>(undefined);

  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isOnline } = useNetworkCheck();
  const { isCheckingIpAccess, isIpAccessAllowed } = useIpAllowList();
  const tenantKey = useTenancy();
  const usersnapContext = useUsersnapApi();
  const { token, setAuthToken, user } = useAuth();
  const isIpAccessBlocked = isOnline && !isCheckingIpAccess && !isIpAccessAllowed;
  const shouldShowIpCheckLoader = isOnline && isCheckingIpAccess;
  const shouldShowLoginForm = isOnline && !isCheckingIpAccess && isIpAccessAllowed;

  const validationSchema = Yup.object().shape({
    id: Yup.string().min(2, 'Id must be minimum 2').required('ID is required'),
  });

  const sanitizeRedirectTarget = (target: string | null | undefined): string | null => {
    if (!target) return null;

    const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(target);
    const isRelativePath = target.startsWith('/');
    const isProtocolRelative = target.startsWith('//');
    const isLoginPath =
      target === AppRoutes.Login || target.startsWith(`${AppRoutes.Login}?`) || target.startsWith(`${AppRoutes.Login}#`);

    if (!isRelativePath || isProtocolRelative || hasScheme || isLoginPath) {
      return null;
    }

    return target;
  };

  const getPostLoginRedirectTarget = (): string => {
    const redirectFromQuery = sanitizeRedirectTarget(searchParams.get('redirect'));

    const locationState = location.state as LoginLocationState | null;
    const statePath = locationState?.from?.pathname ?? '';
    const stateSearch = locationState?.from?.search ?? '';
    const stateHash = locationState?.from?.hash ?? '';
    const redirectFromState = sanitizeRedirectTarget(
      statePath ? `${statePath}${stateSearch}${stateHash}` : null,
    );

    return redirectFromQuery ?? redirectFromState ?? AppRoutes.Roster;
  };

  useEffect(() => {
    if (token && (!isOnline || !isCheckingIpAccess) && (!isOnline || isIpAccessAllowed)) {
      navigate(getPostLoginRedirectTarget(), { replace: true });
      createNotification({
        type: 'info',
        title: `You are signed in as ${user?.name}`,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isOnline, isCheckingIpAccess, isIpAccessAllowed, navigate, location.state, searchParams]);

  const handleSubmit = async (values: LoginFormValues) => {
    if (!tenantKey) return;

    try {
      setSubmitting(true);
      // Clear any previous API errors
      setApiError(undefined);

      const response = await loginAuth({ loginId: values.id, tenantKey });
      await setAuthToken(response.data.token, response.data.refreshToken);
      await trySetUsersnapInitParams();

      setSubmitting(false);
    } catch (error: any) {
      const statusCode = error.response?.status;

      // For login errors, show the API error message or default to "ID does not match."
      if (statusCode === 401) {
        setApiError('ID does not match.');
      } else if (statusCode >= 500) {
        setApiError('Service is temporarily unavailable. Please try again.');
      } else if (statusCode) {
        setApiError(`Login failed (${statusCode}).`);
      } else {
        setApiError('Unable to reach server. Please check your connection and try again.');
      }

      if (statusCode !== 401) {
        console.error('Failed to login', error);
      } else {
        // Keep 401 noise out of console - this is an expected auth failure path.
        console.info('Login failed with invalid credentials');
      }

      if (statusCode !== 401) {
        createNotification({
          type: 'error',
          title: 'Login unavailable',
          description: 'Authentication service is currently unavailable.',
        });
      }
      setSubmitting(false);
    }
  };

  const trySetUsersnapInitParams = async () => {
    if (!usersnapContext) return;

    try {
      const response = await getUserTotalCompletedAssessments();
      const qualifiesForFirstSurvey = response.data >= minAssessmentsForFirstSurvey;

      usersnapContext.setInitParams({
        user: { userId: String(user?.userId), qualifiesForFirstSurvey: qualifiesForFirstSurvey },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div className="w-screen bg-cyan">
      <div className="w-full min-h-dvh flex flex-col items-center mx-auto bg-login-pattern bg-no-repeat bg-contain bg-bottom">
        <div className="flex w-full h-[74px] mb-[39px] p-4 justify-center items-center bg-white-400 dark:bg-gray-900 shadow-header">
          {theme === DARK_MODE_CLASS ? <LogoDarkIcon /> : <LogoIcon />}
        </div>

        {shouldShowLoginForm && (
          <div className="px-6 md:px-12 py-6 md:py-14 flex w-[92%] max-w-[448px] flex-col justify-center items-start gap-8 bg-white-400 dark:border-gray-500 dark:bg-gray-900 shadow-login rounded-2xl">
            <div className="w-full">
              <h2 className="mb-3 flex gap-2 items-center text-gray-900 dark:text-white-400 text-[23px] font-semibold leading-[140%]">
                <LockXLIcon className="[&>g>path]:stroke-sea-serpent" />
                Enter your ID
              </h2>
              <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
                <InputField
                  schema="primary"
                  size="lg"
                  isPasswordInput
                  label="Please enter your ID to begin"
                  className="mb-4 tracking-widest pl-4 pr-12"
                  name="id"
                  value={formik.values.id}
                  onChange={e => {
                    formik.handleChange(e);
                    // Clear API error when user starts typing
                    if (apiError) {
                      setApiError(undefined);
                    }
                  }}
                  errorMessage={apiError || formik.errors.id}
                />
                <BaseButton
                  disabled={!formik.values.id || submitting}
                  type="submit"
                  scheme="cta-primary"
                  size="xl"
                  className="w-full"
                  isLoading={submitting}
                >
                  Continue
                </BaseButton>
              </form>
            </div>
          </div>
        )}

        {shouldShowIpCheckLoader && (
          <div className="pt-4 pb-12 px-4 flex flex-col items-center max-w-[500px] bg-white-400 dark:bg-gray-900 shadow-login rounded-2xl text-center">
            <LockXLIcon className="mt-4 [&>g>path]:stroke-sea-serpent" />
            <h2 className="mt-6 mb-4 text-gray-600 dark:text-white-400 text-[19px] font-semibold leading-7">
              Validating your network access
            </h2>
            <p className="mb-8 px-10 text-gray-600 dark:text-gray-200 text-sm font-normal leading-[140%]">
              Please wait while we confirm this network is approved.
            </p>
          </div>
        )}

        {isIpAccessBlocked && (
          <div className="pt-4 pb-12 px-4 flex flex-col items-center max-w-[500px] bg-white-400 dark:bg-gray-900 shadow-login rounded-2xl text-center">
            <WarningIcon className="mt-4 text-red-400" />
            <h2 className="mt-6 mb-4 text-gray-600 dark:text-white-400 text-[19px] font-semibold leading-7">
              Access is not permitted from your network
            </h2>
            <p className="mb-8 px-10 text-gray-600 dark:text-gray-200 text-sm font-normal leading-[140%]">
              Your network is not approved to access this system. If you believe this is an error, please contact
              support@nascend.com.
            </p>
          </div>
        )}

        {!isOnline && (
          <div className="pt-4 pb-12 px-4 flex flex-col items-center max-w-[500px] bg-white-400 dark:bg-gray-900 shadow-login rounded-2xl text-center">
            <WifiOffIcon className="mt-4" />
            <h2 className="mt-6 mb-4 text-gray-600 dark:text-white-400 text-[19px] font-semibold leading-7">
              Your device is currently offline
            </h2>
            <p className="mb-8 px-10 text-gray-600 dark:text-gray-200 text-sm font-normal leading-[140%]">
              Please check your device settings, or click below to run an offline assessment.
            </p>

            <div className="flex gap-4 w-full justify-center">
              <BaseButton scheme="primary" onClick={() => setShowPatientIdFormModal(true)}>
                Begin
              </BaseButton>
            </div>
          </div>
        )}

        {isShowPatientIdFormModal && <PatientIdFormModal onCancelClick={() => setShowPatientIdFormModal(false)} />}
      </div>
    </div>
  );
};
